import { Task, TaskEvent, TaskPerson, TaskPriority, TaskRepeat, TaskStatus } from "../../services/taskService";

export { apiErrorMessage } from "../answers/answerDeskUtils";

// ── Status & priority ───────────────────────────────────────────────────────

export const STATUSES: TaskStatus[] = ["TODO", "IN_PROGRESS", "WAITING", "DONE"];

export const STATUS_META: Record<TaskStatus, { label: string; color: string; soft: string; hint: string }> = {
    TODO: { label: "To do", color: "#64748b", soft: "#f1f5f9", hint: "Not started" },
    IN_PROGRESS: { label: "In progress", color: "#2563eb", soft: "#eff6ff", hint: "Someone's on it" },
    WAITING: { label: "Waiting", color: "#d97706", soft: "#fffbeb", hint: "Blocked on a customer or supplier" },
    DONE: { label: "Done", color: "#16a34a", soft: "#f0fdf4", hint: "Finished" },
};

export const PRIORITIES: TaskPriority[] = ["URGENT", "HIGH", "NORMAL", "LOW"];

export const PRIORITY_META: Record<TaskPriority, { label: string; color: string; rank: number }> = {
    URGENT: { label: "Urgent", color: "#dc2626", rank: 0 },
    HIGH: { label: "High", color: "#ea580c", rank: 1 },
    NORMAL: { label: "Normal", color: "#94a3b8", rank: 2 },
    LOW: { label: "Low", color: "#0ea5e9", rank: 3 },
};

// ── People ──────────────────────────────────────────────────────────────────

const titleCase = (word: string) => (word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word);

/** "rahim.ahmed@x.com" → "Rahim Ahmed" when no name has been set. */
export function nameFromEmail(email: string): string {
    const local = (email || "").split("@")[0];
    const words = local
        .split(/[._\-+]+/)
        .map((w) => w.replace(/\d+/g, ""))
        .filter(Boolean);
    return words.length ? words.map(titleCase).join(" ") : local || "Someone";
}

export function personName(person: Pick<TaskPerson, "display_name" | "email"> | null | undefined): string {
    if (!person) return "Someone";
    return person.display_name?.trim() || nameFromEmail(person.email);
}

/** First name only — for tight spots like cards and sentences. */
export const firstName = (person: Pick<TaskPerson, "display_name" | "email"> | null | undefined) =>
    personName(person).split(" ")[0];

export function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const AVATAR_COLORS = ["#0f766e", "#7c3aed", "#c2410c", "#0369a1", "#be185d", "#4d7c0f", "#a16207", "#4338ca", "#b91c1c", "#0e7490"];

/** The same person always gets the same colour. */
export function avatarColor(userId: string | null | undefined): string {
    if (!userId) return "#94a3b8";
    let hash = 0;
    for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) | 0;
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ── Calendar dates ("YYYY-MM-DD", the business calendar) ────────────────────

const pad = (n: number) => String(n).padStart(2, "0");

/** Days are compared as UTC midnights, so DST and the browser's zone never shift them. */
const dayNumber = (day: string) => {
    const [y, m, d] = day.split("-").map(Number);
    return Date.UTC(y, m - 1, d) / 86400000;
};

export function addDays(day: string, days: number): string {
    const date = new Date((dayNumber(day) + days) * 86400000);
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export const daysBetween = (from: string, to: string) => dayNumber(to) - dayNumber(from);

export const dayToDate = (day: string) => {
    const [y, m, d] = day.split("-").map(Number);
    return new Date(y, m - 1, d);
};

export const dateToDay = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Today in the browser — only a fallback until the API's "today" arrives. */
export const browserToday = () => dateToDay(new Date());

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function weekdayOf(day: string): number {
    return new Date(dayNumber(day) * 86400000).getUTCDay();
}

/** "4:30 pm" from "16:30:00". */
export function formatTime(time: string | null | undefined): string {
    if (!time) return "";
    const [h, m] = time.split(":").map(Number);
    const suffix = h >= 12 ? "pm" : "am";
    const hour = h % 12 === 0 ? 12 : h % 12;
    return m ? `${hour}:${pad(m)} ${suffix}` : `${hour} ${suffix}`;
}

/** "Today", "Tomorrow", "Yesterday", "Fri", "25 Oct", "25 Oct 2027". */
export function formatDay(day: string, today: string): string {
    const diff = daysBetween(today, day);
    if (diff === 0) return "Today";
    if (diff === 1) return "Tomorrow";
    if (diff === -1) return "Yesterday";
    const [y, m, d] = day.split("-").map(Number);
    if (diff > 1 && diff < 7) return WEEKDAYS[weekdayOf(day)];
    const sameYear = y === Number(today.slice(0, 4));
    return sameYear ? `${d} ${MONTHS[m - 1]}` : `${d} ${MONTHS[m - 1]} ${y}`;
}

export function formatDue(task: Pick<Task, "due_date" | "due_time">, today: string): string {
    if (!task.due_date) return "";
    const day = formatDay(task.due_date, today);
    return task.due_time ? `${day}, ${formatTime(task.due_time)}` : day;
}

export type DueState = "overdue" | "today" | "soon" | "later" | "none";

export function dueState(task: Pick<Task, "due_date" | "status">, today: string): DueState {
    if (!task.due_date) return "none";
    const diff = daysBetween(today, task.due_date);
    if (diff < 0) return task.status === "DONE" ? "later" : "overdue";
    if (diff === 0) return "today";
    if (diff <= 7) return "soon";
    return "later";
}

/** "just now", "12m ago", "3h ago", "Yesterday", "25 Oct". */
export function timeAgo(iso: string, now: Date = new Date()): string {
    const then = new Date(iso);
    const seconds = Math.round((now.getTime() - then.getTime()) / 1000);
    if (seconds < 45) return "just now";
    if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
    if (seconds < 86400 && then.getDate() === now.getDate()) return `${Math.round(seconds / 3600)}h ago`;
    const day = dateToDay(then);
    const today = dateToDay(now);
    if (daysBetween(day, today) === 1) return "Yesterday";
    return formatDay(day, today);
}

export function formatDateTime(iso: string): string {
    const d = new Date(iso);
    return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${formatTime(`${d.getHours()}:${d.getMinutes()}`)}`;
}

// ── Repeat ──────────────────────────────────────────────────────────────────

export function repeatLabel(rule: TaskRepeat | null, interval: number, dueDate?: string | null): string {
    if (!rule) return "Doesn't repeat";
    const n = Math.max(1, interval || 1);
    if (rule === "DAILY") return n === 1 ? "Every day" : `Every ${n} days`;
    if (rule === "WEEKLY") {
        const on = dueDate ? ` on ${WEEKDAYS[weekdayOf(dueDate)]}` : "";
        return (n === 1 ? "Every week" : `Every ${n} weeks`) + on;
    }
    const day = dueDate ? Number(dueDate.slice(8, 10)) : null;
    const on = day ? ` on the ${ordinal(day)}` : "";
    return (n === 1 ? "Every month" : `Every ${n} months`) + on;
}

export function ordinal(n: number): string {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// ── Sorting, filtering, grouping ────────────────────────────────────────────

/** Soonest due first (no date last), then priority, then board order. */
export function byDueThenPriority(a: Task, b: Task): number {
    if (a.due_date !== b.due_date) {
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return a.due_date < b.due_date ? -1 : 1;
    }
    if ((a.due_time || "") !== (b.due_time || "")) {
        if (!a.due_time) return 1;
        if (!b.due_time) return -1;
        return a.due_time < b.due_time ? -1 : 1;
    }
    const p = PRIORITY_META[a.priority].rank - PRIORITY_META[b.priority].rank;
    return p || a.sort_order - b.sort_order;
}

export const byBoardOrder = (a: Task, b: Task) => a.sort_order - b.sort_order || a.create_time.localeCompare(b.create_time);

export type QuickFilter = "mine" | "assigned" | "unassigned" | "overdue" | "today";

export const QUICK_FILTERS: { key: QuickFilter; label: string }[] = [
    { key: "mine", label: "Mine" },
    { key: "assigned", label: "I assigned" },
    { key: "unassigned", label: "Unassigned" },
    { key: "overdue", label: "Overdue" },
    { key: "today", label: "Due today" },
];

export interface TaskFilters {
    q: string;
    quick: QuickFilter | null;
    person: string | null;
    priority: TaskPriority | null;
    label: string | null;
}

export const EMPTY_FILTERS: TaskFilters = { q: "", quick: null, person: null, priority: null, label: null };

export const hasActiveFilters = (f: TaskFilters) => !!(f.q.trim() || f.quick || f.person || f.priority || f.label);

/** Lower-cased text a task can be found by. */
export function searchText(task: Task, people: Map<string, TaskPerson>): string {
    const assignee = task.assignee_user_id ? people.get(task.assignee_user_id) : undefined;
    return [
        task.title,
        task.description,
        task.order_number,
        task.customer_name,
        task.product_name,
        task.labels.join(" "),
        assignee ? personName(assignee) : "",
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
}

export function filterTasks(
    tasks: Task[],
    filters: TaskFilters,
    meId: string | undefined,
    today: string,
    people: Map<string, TaskPerson>,
): Task[] {
    const words = filters.q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return tasks.filter((t) => {
        switch (filters.quick) {
            case "mine":
                if (t.assignee_user_id !== meId) return false;
                break;
            case "assigned":
                if (t.created_by_user_id !== meId || t.assignee_user_id === meId) return false;
                break;
            case "unassigned":
                if (t.assignee_user_id) return false;
                break;
            case "overdue":
                if (dueState(t, today) !== "overdue") return false;
                break;
            case "today":
                if (t.due_date !== today) return false;
                break;
        }
        if (filters.person && t.assignee_user_id !== filters.person) return false;
        if (filters.priority && t.priority !== filters.priority) return false;
        if (filters.label && !t.labels.some((l) => l.toLowerCase() === filters.label!.toLowerCase())) return false;
        if (words.length) {
            const text = searchText(t, people);
            if (!words.every((w) => text.includes(w))) return false;
        }
        return true;
    });
}

export interface TaskSection {
    key: string;
    title: string;
    tasks: Task[];
    tone?: "danger" | "warning" | "muted";
    collapsedByDefault?: boolean;
}

/** "My tasks": what's on my plate, by when it's due. */
export function myTaskSections(tasks: Task[], meId: string | undefined, today: string): TaskSection[] {
    const mine = tasks.filter((t) => t.assignee_user_id === meId);
    const open = mine.filter((t) => t.status !== "DONE").sort(byDueThenPriority);
    const pick = (state: DueState) => open.filter((t) => dueState(t, today) === state);
    const done = mine
        .filter((t) => t.status === "DONE")
        .sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""));
    return [
        { key: "overdue", title: "Overdue", tasks: pick("overdue"), tone: "danger" as const },
        { key: "today", title: "Today", tasks: pick("today"), tone: "warning" as const },
        { key: "soon", title: "Next 7 days", tasks: pick("soon") },
        { key: "later", title: "Later", tasks: pick("later") },
        { key: "none", title: "No date", tasks: pick("none") },
        { key: "done", title: "Recently done", tasks: done, tone: "muted" as const, collapsedByDefault: true },
    ].filter((s) => s.tasks.length > 0);
}

/** Every label in use, most used first — for the label filter and pickers. */
export function labelsInUse(tasks: Task[]): string[] {
    const counts = new Map<string, { label: string; n: number }>();
    tasks.forEach((t) =>
        t.labels.forEach((l) => {
            const key = l.toLowerCase();
            const entry = counts.get(key) || { label: l, n: 0 };
            entry.n += 1;
            counts.set(key, entry);
        }),
    );
    return Array.from(counts.values())
        .sort((a, b) => b.n - a.n || a.label.localeCompare(b.label))
        .map((e) => e.label);
}

// ── Activity sentences ──────────────────────────────────────────────────────

export function describeEvent(
    event: TaskEvent,
    people: Map<string, TaskPerson>,
    today: string,
): string {
    const who = (id: string | null | undefined) => (id ? personName(people.get(id)) : "nobody");
    const p = event.payload || {};
    switch (event.kind) {
        case "created":
            return p.repeat_of ? "created this as the next repeat" : "created this task";
        case "assigned":
            if (!p.to) return `unassigned ${who(p.from)}`;
            return p.from ? `reassigned it from ${who(p.from)} to ${who(p.to)}` : `assigned it to ${who(p.to)}`;
        case "status": {
            const to = STATUS_META[p.to as TaskStatus]?.label ?? p.to;
            if (p.to === "DONE") {
                return p.next_due_date
                    ? `completed it — next one is due ${formatDay(p.next_due_date, today)}`
                    : "completed it";
            }
            if (p.from === "DONE") return `reopened it (${to})`;
            return `moved it to ${to}`;
        }
        case "priority":
            return `set priority to ${PRIORITY_META[p.to as TaskPriority]?.label ?? p.to}`;
        case "title":
            return `renamed it to “${p.to}”`;
        case "description":
            return "updated the description";
        case "labels":
            return (p.to || []).length ? `set labels: ${(p.to || []).join(", ")}` : "removed the labels";
        case "due": {
            const [day, time] = p.to || [];
            if (!day) return "removed the due date";
            return `set the due date to ${formatDue({ due_date: day, due_time: time }, today)}`;
        }
        case "links":
            return "changed the linked records";
        case "repeat":
            return p.rule ? `set it to repeat: ${repeatLabel(p.rule, p.interval).toLowerCase()}` : "stopped it repeating";
        case "checklist_added":
            return `added “${p.text}” to the checklist`;
        case "checklist_done":
            return `ticked “${p.text}”`;
        case "checklist_undone":
            return `unticked “${p.text}”`;
        case "checklist_removed":
            return `removed “${p.text}” from the checklist`;
        case "comment":
            return "commented";
        case "deleted":
            return "deleted it";
        default:
            return event.kind;
    }
}

/** What a notification is about, as a short sentence (actor first). */
export function describeNotification(kind: string, actor: string): string {
    switch (kind) {
        case "assigned":
            return `${actor} assigned you`;
        case "unassigned":
            return `${actor} moved this off your list`;
        case "comment":
            return `${actor} commented on`;
        case "mention":
            return `${actor} mentioned you on`;
        case "completed":
            return `${actor} completed`;
        default:
            return `${actor} updated`;
    }
}
